/**
 * «Код установки» раздела EO «Виджет» (01.11, ТЗ 1.17 EMB-019 ч. 2, §3.3, §0.6 п. 68(4), (7)–(8)): тег загрузчика для «Вставки
 * кода» Tilda — готовый текст с сервера (шаблон — одно место, apps/api/src/utils/widgetV2Install.ts), кнопка «Скопировать» (без
 * https — запасной путь копирования, не вышло — выделить вручную). Адреса — V2_API_HOSTS: основной и запасной, только чтение
 * (смена — выкладкой V4 с «да» владельца); у адреса — экземпляр из сводок или «нет экземпляра». «Последние запросы» по паре
 * «домен сайта × адрес прокладки» — из сводок, без ПДн; старый адрес снимают после 14 дней без запросов (п. 68(8)). В выборе —
 * сайты с v2 и выбранный сайт без v2 (siteChoices); тег показывается, только если ответ — для выбранного сайта (ревью b235d4e4).
 * Г-12 (03.11): вариант тега с адресом без живого экземпляра (сводки нет 15 мин) сервер не выдаёт — здесь причина и кнопка
 * «Выдать всё равно» (POST …/v2/install/force, запись в журнал сайта); выданный так код показывается до смены сайта.
 */
import { useState } from "react";
import { api } from "../../../api/client";
import { copyText } from "../../../lib/clipboard";
import { BASE, failText, useLoad } from "./widgetApi";
import { ago, orenTime, NO_DATA } from "./widgetFmt";
import { pickSite, siteChoices } from "./widgetPick";
import { hostVerdict } from "./widgetStatus";
import type { Install, Overview } from "./widgetTypes";
import css from "./widget.module.css";

const WARN: Readonly<Record<string, string>> = {
  no_hosts: "Адресов кода установки нет: V2_API_HOSTS пуст или с ошибкой. Это переменная выкладки V4 — меняется только выкладкой с «да» владельца.",
  bad_key: "Ключ сайта старого вида (не 64 hex) — перевыпустите ключ в «Сайтах».",
  no_fallback: "Нет запасного адреса: в V2_API_HOSTS один хост. Код ниже — без запасного; при блокировке основного виджет не откроется.",
};

function TagBlock({ title, note, tag }: { title: string; note: string; tag: string }) {
  const [msg, setMsg] = useState("");
  return (
    <div className={css.block}>
      <div className={css.sub}>{title}</div>
      <span className={css.muted}>{note}</span>
      <pre className={css.code}>{tag}</pre>
      <div className={css.line}>
        <button type="button" className={css.btn} onClick={() => void copyText(tag).then((ok) => setMsg(ok ? "Скопировано" : "Не удалось скопировать — выделите текст и скопируйте вручную"))}>Скопировать</button>
        {msg ? <span className={css.toast} role="status">{msg}</span> : null}
      </div>
    </div>
  );
}

export function WidgetInstall({ siteId, onSite }: { siteId: number | null; onSite: (id: number | null) => void }) {
  const ov = useLoad<Overview>(`${BASE}/v2/overview`);
  const sites = siteChoices(ov.data?.sites ?? [], siteId);
  const id = pickSite(sites, siteId, ov.data !== null);
  const ins = useLoad<Install>(id === null ? null : `${BASE}/${id}/v2/install`);
  const [forced, setForced] = useState<Install | null>(null);
  const [forceErr, setForceErr] = useState("");
  const got = forced && forced.site.id === id ? forced : ins.data;
  const d = got && got.site.id === id ? got : null, now = Date.now();
  const force = () => {
    if (id === null) return;
    setForceErr("");
    api<Install>(`${BASE}/${id}/v2/install/force`, { method: "POST" }).then(setForced, (e: unknown) => setForceErr(failText(e, "Не удалось выдать код")));
  };
  return (
    <div className={css.card}>
      <div className={css.head}>
        <span className={css.title}>Код установки</span>
        <div className={css.line}>
          <select className={css.select} value={id ?? ""} aria-label="Сайт" onChange={(e) => onSite(e.target.value ? Number(e.target.value) : null)}>
            {!sites.length ? <option value="">нет сайтов v2</option> : null}
            {sites.map((s) => <option key={s.id} value={s.id}>{s.domain} (№{s.id}){s.v2 ? "" : " — v2 не настроен"}</option>)}
          </select>
          <button type="button" className={css.ghost} disabled={ins.busy} onClick={() => { setForced(null); ins.reload(); }}>Обновить</button>
        </div>
      </div>
      {ov.err || ins.err ? <div className={css.err}>{ov.err || ins.err}</div> : null}
      {d ? (
        <>
          {d.warning ? <p className={css.warnLine}>{WARN[d.warning] ?? d.warning}</p> : null}
          {!d.site.enabled || !d.site.active ? <p className={css.warnLine}>{!d.site.active ? "Ключ сайта отозван" : "v2 у сайта выключен"}: код можно поставить заранее, заявки пойдут после включения.</p> : null}
          <div className={css.block}>
            <div className={css.sub}>Адреса прокладки (V2_API_HOSTS, только чтение)</div>
            {d.hosts.map((h) => {
              const v = hostVerdict(h);
              return (
                <div key={h.host} className={css.line}>
                  <span className={`${css.badge} ${css[v.tone]}`}>{h.role === "primary" ? "основной" : "запасной"}: {v.label}</span>
                  <b className={css.mono}>{h.host}</b>
                  <span className={css.muted}>{h.instances.length ? `экземпляр ${h.instances.join(", ")} · сводка ${orenTime(h.last_at)}` : v.reasons.join("; ")}</span>
                </div>
              );
            })}
          </div>
          {d.held?.length && !d.forced ? (
            <div className={css.block}>
              <p className={css.warnLine}>Код с адресом {d.held.join(", ")} не выдан: за адресом нет живого экземпляра прокладки (сводки нет 15 мин) — виджет с таким кодом не откроется.</p>
              <div className={css.line}><button type="button" className={css.btn} onClick={force}>Выдать всё равно</button><span className={css.muted}>запишется в журнал сайта</span></div>
              {forceErr ? <div className={css.err}>{forceErr}</div> : null}
            </div>
          ) : null}
          {d.forced ? <p className={css.warnLine}>Код выдан без живого экземпляра ({d.held?.join(", ")}) — записано в журнал сайта.</p> : null}
          {d.tags.two ? <TagBlock title="С запасным адресом — обычный случай" note="Tilda: «Настройки сайта → Вставка кода → HTML-код для вставки внутрь head», затем «Опубликовать все страницы»." tag={d.tags.two} /> : null}
          {d.tags.single ? <TagBlock title={d.tags.two ? "Один адрес — для сайтов с CSP без 'unsafe-inline'" : "Код установки (один адрес)"} note="Без встроенного onerror: запасного адреса у этого варианта нет." tag={d.tags.single} /> : null}
          {!d.phone_in_tag && d.tags.single ? <span className={css.muted}>Телефона в теге нет: в конфиге нет contacts.phone вида +7XXXXXXXXXX — аварийная панель возьмёт встроенную копию контактов.</span> : null}
          <div className={css.block}>
            <div className={css.sub}>Последние запросы: домен сайта × адрес прокладки (из сводок)</div>
            <div className={css.tableWrap}>
              <table className={css.table}>
                <thead><tr><th>домен</th><th>адрес прокладки</th><th>экземпляр</th><th>последний запрос</th><th /></tr></thead>
                <tbody>
                  {d.domains.map((dom) => {
                    const rows = d.seen.filter((x) => x.domain === dom);
                    if (!rows.length) return <tr key={dom}><td>{dom}</td><td colSpan={4} className={css.muted}>{NO_DATA}: в сводках запросов с этого домена нет</td></tr>;
                    return rows.map((x) => (
                      <tr key={`${dom}-${x.host}-${x.instance}`}>
                        <td>{dom}</td><td className={css.mono}>{x.host}</td><td>{x.instance}</td>
                        <td>{orenTime(x.last_at)} ({ago(x.last_at, now)})</td>
                        <td className={css.muted}>{x.in_code ? "в коде" : "снят из кода — снимается после 14 дней без запросов"}</td>
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
            <span className={css.muted}>Полнота наблюдения за 14 дней (окна сводок без разрывов): {NO_DATA} — считает следующий шаг V4 (13б).</span>
          </div>
        </>
      ) : !ins.err ? <div className={css.muted}>{id === null ? "Сайтов v2 нет — заведите сайт и конфиг в «Сайтах»" : "Загружаю…"}</div> : null}
    </div>
  );
}
